import { useEffect, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useLayoutContext } from "../../layouts/general-layout";
import { RouterConstant } from "../../routers/index";
import Button from '../../components/button';
import "./user-registration-step1.scss";
import { PointIcon } from "../../assets/images";
import { useUserStore } from '../../utils/userStore';
import { LoginService } from '../../services/login/login.services'; // Add this at top
import LoginHeader from "../../components/login-header";
import PasswordInput from "../../components/password-input";


export default function Index() {
  const { t } = useLayoutContext();
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;
  const loginService = new LoginService()
  const { userInfo: formData, setUserInfo: setData } = useUserStore();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;

  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  });

  const [touched, setTouched] = useState({
    first_name: false,
    last_name: false,
    email: false,
    password: false
  });

  const validateField = (field: 'first_name' | 'last_name' | 'email' | 'password') => {
    const newErrors = { ...errors };

    if (field === 'first_name') {
      if (!formData.first_name?.trim()) {
        newErrors.first_name = t('enterfirstName');
      } else if (formData.first_name.length > 20) {
        newErrors.first_name = t('nameContainUpTo20');
      } else {
        newErrors.first_name = '';
      }
    }

    if (field === 'last_name') {
      if (!formData.last_name?.trim()) {
        newErrors.last_name = t('enterlastName');
      } else if (formData.last_name.length > 20) {
        newErrors.last_name = t('nameContainUpTo20');
      } else {
        newErrors.last_name = '';
      }
    }

    if (field === 'email') {
      if (!formData.email?.trim()) {
        newErrors.email = t('enterEmail');
      } else if (!emailRegex.test(formData.email)) {
        newErrors.email = t('emailInvalid');
      } else {
        newErrors.email = '';
      }
    }

    if (field === 'password') {
      if (!formData.password?.trim()) {
        newErrors.password = t('enterPassword');
      } else if (!passwordRegex.test(formData.password)) {
        newErrors.password = t('passwordValidationFailed');
      } else {
        newErrors.password = '';
      }
    }

    setErrors(newErrors);
  };

  const validateForm = () => {
    const fields: ('first_name' | 'last_name' | 'email' | 'password')[] = ['first_name', 'last_name', 'email', 'password'];
    let valid = true;

    fields.forEach(field => {
      validateField(field);
      if (errors[field]) valid = false;
    });

    return valid;
  };

  const handleBlur = (field: 'first_name' | 'last_name' | 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const handleInputChange = (field: 'first_name' | 'last_name' | 'email' | 'password', value: string) => {
    setData({ [field]: value });
    if ((field === 'first_name' || field === 'last_name') && !touched[field]) {
      setTouched(prev => ({ ...prev, [field]: true }));
    }
  };

  const handleUserTypeChange = (type: 'school' | 'casual') => {
    const userTypeValue = type === 'school' ? 4 : 3;
    setData({ userType: userTypeValue });
  };

  const handleBackClick = () => {
    navigate(ROUTERS_PATH.USERREGISTRATIONINITIAL);
  };

  const handleRequestOTP = async () => {
    setTouched({ first_name: true, last_name: true, email: true, password: true });
    const isValid = validateForm();
    if (!isValid) return;

    try {
      setIsChecking(true)
      await loginService.emailValidation(formData.email ?? '', formData.password ?? '');
      navigate(ROUTERS_PATH.USERREGISTRATIONOTP);
    } catch (err: any) {
      const code = err.response?.data?.detail?.error_code;
      const newErrors = { ...errors };
      if (code === 1) {
        newErrors.email = t("emailInvalid");
      } else if (code === 3) {
        newErrors.email = t("emailAlreadyExist");
      } else if (code === 4) {
        newErrors.password = t("passwordValidationFailed");
      }
      setErrors(newErrors);
    } finally {
      setIsChecking(false)
    }
  };

  return (
    <div className="full-head-template register-page">
      <LoginHeader handleBackClick={handleBackClick} title={t('registerWithEmailAndPassword')} subtitle={t('mustFillDetails')} noImg />
      <div className='register-page-form'>
        <div className='register-page-form-wrapper'>
          <div className="register-page-form-fields">
            <div className="form-field">
              <input
                type="text"
                placeholder={`*${t('firstName')}`}
                value={formData.first_name || ''}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                onBlur={() => handleBlur('first_name')}
              />
              {touched.first_name && errors.first_name && (
                <div className="error-text">{errors.first_name}</div>
              )}
            </div>
            <div className="form-field">
              <input
                type="text"
                placeholder={`*${t('lastName')}`}
                value={formData.last_name || ''}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                onBlur={() => handleBlur('last_name')}
              />
              {touched.last_name && errors.last_name && (
                <div className="error-text">{errors.last_name}</div>
              )}
            </div>
            <div className="form-field-container">
              <div className="form-field">
                <input
                  type="email"
                  placeholder={`*${t('email')}`}
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                />
                {touched.email && errors.email && (
                  <div className="error-text">{errors.email}</div>
                )}
              </div>
            </div>
            <PasswordInput
              placeholder={`*${t('password')}`}
              value={formData.password || ""}
              onChange={(value) => handleInputChange('password', value)}
              onBlur={() => handleBlur('password')}
              error={touched.password ? errors.password : ""}
              infoText={t("passwordInputInfo")}
              icon={PointIcon}
            />
          </div>

          <div className='user-type-container'>
            <p>{t('whatKindOfUser')}</p>
            <div className='user-type-container-inner'>
              <Button
                text={t('casualUser')}
                outline={formData.userType !== 3}
                onClick={() => handleUserTypeChange('casual')}
                size='sm'
              />
              <Button
                text={t('throughSchool')}
                outline={formData.userType !== 4}
                onClick={() => handleUserTypeChange('school')}
                size='sm'
              />
            </div>
          </div>
        </div>
      </div>
      <div className="login-page-form-action">
        <p>
          {t("haveAnAccount")} <Link to={ROUTERS_PATH.LOGIN}>{t("login")}</Link>
        </p>
        <Button type="submit" text={t("next")} color="blue"
          onClick={handleRequestOTP}
          disabled={isChecking}
          loader={isChecking} />
      </div>
    </div>
  );
}
